import type { en } from "./en";

/**
 * German.
 *
 * Typed against the English catalogue, so a missing key is a compile error
 * rather than a screen in two languages at once. Formal throughout — this is a
 * workplace tool, and the people using it are colleagues rather than customers.
 */
export const de: Record<keyof typeof en, string> = {
  "app.name": "Spontar",
  "app.admin": "Verwaltung",

  "nav.dashboard": "Übersicht",
  "nav.institutions": "Einrichtungen",
  "nav.users": "Personen",
  "nav.legal": "Rechtsdokumente",
  "nav.ai": "Assistenzkosten",
  "nav.retention": "Aufbewahrung",
  "nav.health": "Betriebszustand",
  "nav.migrations": "Migrationen",
  "nav.support": "Supportzugriff",
  "nav.enquiries": "Anfragen",
  "nav.enquiries.unanswered": "{count} unbeantwortet",
  "nav.signOut": "Abmelden",
  "nav.menu": "Menü",
  "nav.skipToContent": "Zum Inhalt springen",

  "zone.label": "Zone",
  "zone.eu": "EU",
  "zone.us": "US",
  "zone.explain":
    "Auf welche Installation diese Konsole wirkt. Jede Einrichtung liegt in genau einer, und ein Wechsel ist eine Migration und keine Einstellung.",

  "auth.signIn": "Anmelden",
  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "auth.signingIn": "Anmeldung läuft …",
  "auth.staffOnly": "Diese Konsole ist für Spontar-Mitarbeitende.",

  "mfa.title": "Zweiten Faktor einrichten",
  "mfa.body":
    "Diese Konsole kann die Daten jeder Einrichtung lesen und ändern, wozu jede berechtigt ist. Ein Passwort allein trägt das nicht — deshalb ist ein zweiter Faktor nötig, bevor irgendetwas anderes möglich ist.",
  "mfa.phone": "Telefonnummer",
  "mfa.phoneHint": "Bei jeder Anmeldung kommt ein Code per SMS.",
  "mfa.send": "Code senden",
  "mfa.code": "Der Code",
  "mfa.verify": "Einrichtung abschließen",
  "mfa.enrolled": "Eingerichtet. Bitte melden Sie sich erneut an — ab jetzt werden Sie nach einem Code gefragt.",
  "mfa.required": "Richten Sie zuerst einen zweiten Faktor ein.",
  "mfa.signOutInstead": "Stattdessen abmelden",
  "mfa.reauth.title": "Bitte bestätigen Sie, dass Sie es sind",
  "mfa.reauth.body":
    "Ihre Anmeldung liegt eine Weile zurück. Einen zweiten Faktor einzurichten ist sensibel genug, dass wir zuvor noch einmal Ihr Passwort brauchen.",
  "mfa.reauth.confirm": "Bestätigen und fortfahren",
  "mfa.reauth.wrong": "Dieses Passwort stimmt nicht.",

  "error.notAdmin.title": "Nicht für Sie",
  "error.notAdmin.body":
    "Diese Konsole ist für Spontar-Mitarbeitende. Wenn Sie eine Einrichtung verwalten, arbeiten Sie in Ihrem eigenen Backoffice.",
  "error.notAdmin.toBackOffice": "Zum Backoffice",

  "mfa.unverified.title": "Ihre Adresse ist nicht bestätigt",
  "mfa.unverified.body":
    "An eine unbestätigte Adresse lässt sich kein zweiter Faktor binden. Das ist eine Regel von Firebase, nicht von uns, und sie ist sinnvoll: Ohne sie könnte jemand, der das Konto übernimmt, die eigentliche Inhaberin aussperren.",
  "mfa.unverified.send": "Link senden",
  "mfa.unverified.sent": "Gesendet",
  "mfa.unverified.then": "Öffnen Sie ihn und beginnen Sie hier noch einmal.",
  "mfa.verificationSent": "Bitte sehen Sie in Ihr Postfach.",
  "auth.mfa.title": "Noch ein Schritt",
  "auth.mfa.body": "Ein Code muss an Ihr Telefon mit der Endung {hint} gehen, bevor Sie hineinkommen.",
  "auth.mfa.send": "Code senden",
  "auth.mfa.code": "Code",
  "auth.mfa.verify": "Anmelden",
  "auth.mfa.startOver": "Neu beginnen",
  "error.auth.invalidCode": "Dieser Code stimmt nicht. Fordern Sie einen neuen an, wenn er abgelaufen ist.",

  "common.save": "Speichern",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.confirm": "Bestätigen",
  "common.loading": "Wird geladen …",
  "common.retry": "Erneut versuchen",
  "common.language": "Sprache",
  "common.locale.de": "Deutsch",
  "common.locale.en": "English",
  "billing.plan.title": "Tarif",
  "tier.solo": "Solo — 1 Lehrkraft",
  "tier.small": "Klein — bis zu 3 Lehrkräfte",
  "tier.medium": "Mittel — bis zu 10 Lehrkräfte",
  "tier.large": "Groß — bis zu 40 Lehrkräfte",
  "common.search": "Suchen",
  "common.none": "Keine",
  "common.back": "Zurück",
  "common.required": "Erforderlich",
  "common.optional": "Optional",
  "common.saved": "Gespeichert",
  "common.add": "Hinzufügen",
  "common.edit": "Bearbeiten",
  "common.delete": "Löschen",
  "common.yes": "Ja",
  "common.no": "Nein",

  "dashboard.title": "Übersicht",
  "dashboard.welcome": "Guten Tag, {name}.",

  "audit.reason": "Grund",
  "audit.reasonHint":
    "Wird dauerhaft unter Ihrem Namen im Protokoll festgehalten. Ein Satz, den eine Kollegin in einem Jahr lesen und verstehen kann.",
  "audit.reasonRequired": "Für alles Zerstörerische ist ein Grund erforderlich.",

  "error.unexpected": "Etwas ist schiefgelaufen. Der Fehler wurde protokolliert.",
  "error.network.unavailable": "Der Server war nicht erreichbar. Bitte erneut versuchen.",
  "error.auth.required": "Bitte melden Sie sich an.",
  "error.auth.invalidCredentials": "E-Mail und Passwort passen nicht zusammen.",
  "error.auth.staleLogin": "Bitte geben Sie aus Sicherheitsgründen Ihr Passwort erneut ein, bevor Sie einen zweiten Faktor einrichten.",
  "error.permission.denied": "Dafür fehlt Ihnen die Berechtigung.",
  "error.permission.disabled": "Dieses Konto ist deaktiviert.",
  "error.permission.noInstitution": "Dieses Konto gehört zu keiner Einrichtung.",
  "error.validation.invalidInput": "In diesem Formular stimmt etwas nicht.",
  "error.validation.studentSeatsTooFew": "Mindestens ein Schüler-Platz.",
  "error.validation.studentSeatsTooMany": "Eine Lizenz kann höchstens 10.000 Schüler-Plätze erlauben.",
  "error.user.notStaff": "Einen Anmeldelink bekommen nur Lehrkräfte und die Leitung.",
  "error.user.noEmail": "Diese Person hat keine E-Mail-Adresse, an die ein Link gehen könnte.",
  "error.user.noAuthAccount":
    "Hinter dieser Person steht kein Anmeldekonto. Vergeben Sie stattdessen ein neues Passwort.",
  "error.notFound.title": "Hier ist nichts",
  "error.notFound.body": "Der Link ist vielleicht alt, oder die Seite ist umgezogen.",

  "metrics.institutions": "Einrichtungen",
  "metrics.institutions.detail": "Aktiv, gesperrt, abgelaufen — und wo sie sind",
  "metrics.institutions.active": "Aktiv",
  "metrics.institutions.suspended": "Gesperrt",
  "metrics.institutions.lapsed": "Lizenz abgelaufen",
  "metrics.institutions.byCountry": "Nach Land",

  "metrics.seats": "Verkaufte Plätze",
  "metrics.seats.detail": "Was verkauft wurde, nach Tarif",
  "metrics.seats.tier": "Tarif",
  "metrics.seats.sold": "Plätze",
  "metrics.seats.institutions": "Einrichtungen",

  "metrics.sessions": "Stunden diesen Monat",
  "metrics.sessions.detail": "Diese Woche, dieser Monat und wie verbunden wurde",
  "metrics.sessions.week": "Diese Woche",
  "metrics.sessions.month": "Dieser Monat",
  "metrics.sessions.online": "Online",
  "metrics.sessions.offline": "Offline",

  "metrics.runsPerSession": "Szenen pro Stunde",
  "metrics.runsPerSession.detail": "Die Verteilung — darauf kommt es an",
  "metrics.runsPerSession.why":
    "Eine Stunde mit einer Szene ist jemand, der das Produkt ausprobiert. Vier sind jemand, der damit unterrichtet. Eine steigende Zahl von Stunden kann einen Rückgang hier verdecken — deshalb ist das die Zahl, die man beobachtet.",
  "metrics.runsPerSession.runs": "Szenen",
  "metrics.runsPerSession.sessions": "Stunden",

  "metrics.ai": "Assistenzkosten diesen Monat",
  "metrics.ai.detail": "Nach Funktion, im Vergleich zum Vormonat",
  "metrics.ai.previous": "Vormonat",
  "metrics.ai.feature": "Funktion",
  "metrics.ai.cost": "Kosten",
  "metrics.ai.refused": "Von der Grenze abgewiesen",

  "metrics.empty": "Noch nichts zu zeigen",
  "metrics.openDetail": "Aufschlüsselung anzeigen",

  "alerts.retentionOverdue":
    "{count} Stunden sind über ihre Aufbewahrungsfrist hinaus und wurden nicht gelöscht.",
  "alerts.retentionMissed": "Der Löschauftrag hat am letzten Tag keinen Lauf abgeschlossen.",
  "alerts.aiBoundaryViolation":
    "{count} Anfragen an den Assistenten wurden abgewiesen, weil sie Daten von Teilnehmenden enthielten. Das dürfte nicht möglich sein — behandeln Sie es als P1.",
  "alerts.aiBudgetExhausted":
    "{count} Einrichtungen haben das Assistenzbudget dieses Monats aufgebraucht.",
  "alerts.legalGap":
    "{count} erforderliche Dokumente gelten für Kundschaft und sind nicht veröffentlicht. Das blockiert einen Abschluss, nicht eine Stunde.",

  "institutions.title": "Einrichtungen",
  "institutions.new": "Einrichtung anlegen",
  "institutions.empty": "Noch keine Einrichtung",
  "institutions.column.name": "Name",
  "institutions.column.country": "Land",
  "institutions.column.zone": "Zone",
  "institutions.column.segment": "Art",
  "institutions.column.tier": "Tarif",
  "institutions.column.seats": "Plätze",
  "institutions.column.validUntil": "Läuft bis",
  "institutions.column.status": "Status",
  "institutions.filter.status": "Status",
  "institutions.filter.country": "Land",
  "institutions.filter.tier": "Tarif",
  "institutions.filter.all": "Alle",
  "institutions.state.active": "Aktiv",
  "institutions.state.expiring": "Läuft bald aus",
  "institutions.state.lapsed": "Abgelaufen",
  "institutions.state.suspended": "Gesperrt",
  "institutions.segment.studio": "Studio",
  "institutions.segment.school": "Schule",

  "institution.new.title": "Einrichtung anlegen",
  "institution.new.name": "Name",
  "institution.new.country": "Land",
  "institution.new.zone": "Zone",
  "institution.new.zoneDerived":
    "Ergibt sich aus dem Land, wird nie gewählt. Die Zone bestimmt, wo die Daten liegen und welches Recht sie erreicht.",
  "institution.new.zoneUsUnavailable":
    "Die US-Installation gibt es noch nicht. Einrichtungen in den USA und Kanada liegen derzeit in der EU-Installation — das ist eine Entscheidung, die man ihnen sagen sollte, statt sie entdecken zu lassen.",
  "institution.new.segment": "Art der Einrichtung",
  "institution.new.locale": "Sprache",
  "institution.new.tier": "Tarif",
  "institution.new.seats": "Lehrkraft-Plätze",
  "institution.new.students": "Schüler-Plätze",
  "institution.new.studentsHint":
    "Wie viele Schülerinnen und Schüler die Lizenz erlaubt. Lehrkräfte und die Leitung zählen nicht mit.",
  "institution.new.validUntil": "Läuft bis",
  "institution.new.ownerName": "Name der Leitung",
  "institution.new.ownerEmail": "E-Mail der Leitung",
  "institution.new.billingEmail": "Wohin Rechnungen gehen",
  "institution.new.create": "Einrichtung anlegen",
  "institution.new.created": "{name} angelegt.",
  "institution.new.linkSent": "Ein Anmeldelink ist unterwegs an {email}.",
  "institution.new.linkNotSent":
    "{name} wurde angelegt, aber der Anmeldelink konnte nicht an {email} geschickt werden. Öffnen Sie die Einrichtung und drücken Sie „Anmeldelink senden“.",

  "institution.tab.overview": "Überblick",
  "institution.tab.licence": "Lizenz",
  "institution.tab.usage": "Nutzung",
  "institution.tab.users": "Personen",
  "institution.tab.legal": "Rechtliches",
  "institution.tab.danger": "Gefahrenzone",

  "institution.overview.created": "Angelegt",
  "institution.overview.billingEmail": "Rechnungen gehen an",
  "institution.overview.vat": "Umsatzsteuer-Identifikationsnummer",
  "institution.overview.vatValidated": "Geprüft am {date}",
  "institution.overview.vatNone": "Keine hinterlegt",
  "institution.overview.manualBilling": "Abrechnung per Rechnung",
  "institution.overview.stripe": "Abrechnung per Karte",

  "institution.licence.title": "Lizenz von Hand setzen",
  "institution.licence.body":
    "Das ist der gesamte Abrechnungsweg für eine Schule, die per Überweisung zahlt. Das Geld kommt an, und jemand setzt hier das Datum. Ein eigenes Rechnungssystem gibt es nicht.",
  "institution.licence.tier": "Tarif",
  "institution.licence.seats": "Lehrkraft-Plätze",
  "institution.licence.students": "Schüler-Plätze",
  "institution.licence.studentsUnset":
    "Noch nicht gesetzt. Bis dahin erlaubt diese Lizenz beliebig viele Schülerinnen und Schüler — setzen Sie jetzt eine Zahl.",
  "institution.licence.validUntil": "Läuft bis",
  "institution.licence.apply": "Setzen",
  "institution.licence.applied": "Lizenz gesetzt.",
  "institution.licence.seatsExceedTier": "Das sind mehr Plätze, als der Tarif {tier} erlaubt.",
  "institution.licence.seatsTooFew": "Mindestens ein Platz — die Leitung belegt ihn.",
  "institution.licence.studentsRequired": "Wie viele Schülerinnen und Schüler darf diese Lizenz umfassen? Pflichtfeld.",
  "institution.licence.studentsTooFew": "Mindestens einer.",
  "institution.licence.studentsTooMany": "Eine Lizenz kann höchstens {max} Schüler-Plätze erlauben.",

  "institution.usage.teacherSeats": "Lehrkraft-Plätze",
  "institution.usage.teachersUsed": "Lehrkräfte, einschließlich der Leitung",
  "institution.usage.studentSeats": "Schüler-Plätze",
  "institution.usage.studentsUsed": "Schülerinnen und Schüler im Register",
  "institution.usage.unlimited": "Keine Grenze gesetzt",

  "institution.access.title": "Anmeldung der Leitung",
  "institution.access.body":
    "Die Leitung vergibt ihr Passwort über einen Link an ihre Adresse; nichts anderes öffnet das Konto. Schicken Sie ihn erneut, wenn die Mail nicht angekommen oder der Link abgelaufen ist — er gilt eine Stunde.",
  "institution.access.send": "Anmeldelink senden",
  "institution.access.sent": "Ein Anmeldelink ist unterwegs an {email}.",
  "institution.access.notSent":
    "Der Anmeldelink konnte nicht an {email} geschickt werden. Der Link wurde erzeugt, die Mail ging nicht raus — prüfen Sie die Mail-Konfiguration und versuchen Sie es erneut.",
  "institution.access.noOwner": "Keine Leitung mit E-Mail-Adresse.",

  "institution.users.column.name": "Name",
  "institution.users.column.role": "Rolle",
  "institution.users.column.username": "Benutzername",
  "institution.users.column.status": "Status",
  "institution.users.noContent":
    "Personen und ihre Zugänge. Nichts von dem, was sie geschrieben haben — eine Szene, eine Notiz, eine Bewertung — ist von hier aus erreichbar.",

  "institution.legal.applies": "Gilt",
  "institution.legal.accepted": "Angenommen",
  "institution.legal.outstanding": "Offen",
  "institution.legal.blocking": "{count} offen — das blockiert Stunden",
  "institution.legal.ours":
    "{count} Dokumente, die hier gelten, hat Spontar nie veröffentlicht. Diese Einrichtung kann keine Stunde starten, und niemand dort kann etwas daran ändern.",
  "institution.legal.theirs":
    "{count} Dokumente sind veröffentlicht und warten darauf, dass diese Einrichtung sie annimmt.",
  "institution.legal.unknown":
    "Diese Einrichtung fehlt im Abdeckungsbericht. Das sollte nicht passieren — prüfen Sie die Rechtsbibliothek.",
  "institution.legal.version": "Fassung {version}",
  "institution.legal.blockedGroups":
    "{count} aktive Gruppen können keine Stunde starten, solange das offen ist.",
  "institution.legal.toCoverage": "Abdeckungsbericht öffnen",
  "institution.legal.clear": "Alles Geltende ist angenommen",

  "institution.danger.suspend": "Sperren",
  "institution.danger.reactivate": "Entsperren",
  "institution.danger.suspendTitle": "{name} sperren",
  "institution.danger.suspendBody":
    "Neue Stunden lassen sich nicht starten. Eine laufende Stunde bleibt unberührt, und gelöscht wird nichts — jede Szene, Gruppe und Statistik bleibt genau so, und das Entsperren stellt alles auf einmal wieder her.",
  "institution.danger.suspendConfirm": "Zur Bestätigung {name} eingeben",
  "institution.danger.suspended": "Gesperrt.",
  "institution.danger.reactivated": "Entsperrt.",

  "institution.danger.exportFirst": "Vorher alles herunterladen",
  "institution.danger.exportBody":
    "Eine Einrichtung, die geht, hat Anspruch auf das, was sie geschaffen hat. Laden Sie es vor dem Löschen herunter — danach gibt es nichts mehr zu holen.",
  "institution.danger.exported": "Heruntergeladen.",

  "institution.danger.delete": "Diese Einrichtung löschen",
  "institution.danger.deleteTitle": "{name} löschen",
  "institution.danger.deleteBody":
    "Jedes Konto, jede Gruppe, jede Szene, jede Stunde und jede Statistik dieser Einrichtung wird entfernt, und niemand aus ihr kann sich mehr anmelden. Das kann niemand rückgängig machen, wir eingeschlossen. Der Protokolleintrag und der Grund, den Sie eingeben, sind das Einzige, was bleibt.",
  "institution.danger.deleteConfirm": "Zur Bestätigung {name} eingeben",
  "institution.danger.deleted": "{name} wurde gelöscht.",
  "institution.danger.mismatch": "Das ist nicht der Name.",

  "enquiries.title": "Anfragen",
  "enquiries.intro":
    "Was über das Kontaktformular der Website eintrifft. Die Seite verspricht, dass ein Mensch innerhalb eines Arbeitstags antwortet; markieren Sie eine Anfrage als beantwortet, sobald Sie es getan haben, damit niemand zweimal antwortet und nichts unbemerkt liegen bleibt.",
  "enquiries.unanswered": "{count} unbeantwortet",
  "enquiries.showClosed": "Geschlossene anzeigen",
  "enquiries.hideClosed": "Geschlossene ausblenden",
  "enquiries.empty": "Bisher ist nichts eingetroffen",
  "enquiries.failed": "Die Anfragen konnten nicht geladen werden.",
  "enquiries.column.received": "Eingegangen",
  "enquiries.column.from": "Von",
  "enquiries.column.about": "Worum es geht",
  "enquiries.column.message": "Nachricht",
  "enquiries.column.status": "Status",
  "enquiries.column.actions": "Aktionen",
  "enquiries.status.new": "Unbeantwortet",
  "enquiries.status.answered": "Beantwortet",
  "enquiries.status.closed": "Geschlossen",
  "enquiries.action.answered": "Als beantwortet markieren",
  "enquiries.action.close": "Schließen",
  "enquiries.action.reopen": "Wieder öffnen",
  "enquiries.reply": "Per E-Mail antworten",
  "enquiries.urgent": "Datenschutz — zuerst beantworten",
  "enquiries.subject.general": "Allgemein",
  "enquiries.subject.studios": "Einsatz im Studio",
  "enquiries.subject.schools": "Einsatz in der Schule",
  "enquiries.subject.dataProtection": "Datenschutz",
  "enquiries.subject.press": "Presse",
  "enquiries.segment.studio": "Studio",
  "enquiries.segment.school": "Schule",
  "enquiries.segment.other": "Sonstiges",
  "enquiries.updated": "Als {status} markiert.",
  "enquiries.error.invalid": "Diese Änderung kann diese Seite nicht vornehmen.",
  "enquiries.error.notFound": "Diese Anfrage wurde nicht gefunden.",

  "support.title": "Supportzugriff",
  "support.intro":
    "Spontar kann eine Einrichtung nicht aus eigener Machtvollkommenheit betreten. Sie fragen; die Leitung sagt ja oder nein; der Zugriff ist nur lesend und endet von selbst. Durchgesetzt wird das von den Sicherheitsregeln, nicht von dieser Seite.",
  "support.request": "Zugriff anfragen",
  "support.institution": "Einrichtung",
  "support.reason": "Wofür Sie ihn brauchen",
  "support.reasonHint":
    "Die Leitung liest das und entscheidet. Sagen Sie, was Sie beheben wollen und was Sie sich ansehen werden — mindestens ein Satz.",
  "support.duration": "Für wie lange",
  "support.duration.1": "Eine Stunde",
  "support.duration.8": "Einen Arbeitstag",
  "support.duration.24": "Einen Tag",
  "support.send": "Anfrage senden",
  "support.sent": "Angefragt. {count} Leitungen wurden benachrichtigt.",
  "support.readOnly":
    "Immer nur lesend. Es gibt überhaupt keinen Schreibweg — keinen versteckten Knopf und keinen deaktivierten.",
  "support.noOperational":
    "Was eine Lehrkraft über eine Stunde festhält — Notizen, Bewertungen — bleibt bei ihr. Der Zugriff reicht bis zur Konfiguration und den Inhalten einer Einrichtung und endet dort.",

  "support.state.pending": "Wartet auf die Leitung",
  "support.state.approved": "Offen",
  "support.state.denied": "Abgelehnt",
  "support.state.revoked": "Zurückgezogen",
  "support.state.expired": "Beendet",

  "support.column.institution": "Einrichtung",
  "support.column.reason": "Grund",
  "support.column.asked": "Angefragt",
  "support.column.state": "Status",
  "support.column.remaining": "Rest",
  "support.empty": "Es hat niemand Zugriff angefragt",
  "support.remaining": "Noch {count} Minuten",

  "support.active.banner":
    "Sie sind in {institution}, noch {minutes} Minuten. Angegebener Grund: {reason}",
  "support.active.readOnly": "Nur lesend.",

  "support.error.requestNotFound": "Diese Anfrage wurde nicht gefunden.",
  "support.error.alreadyDecided": "Diese Anfrage ist bereits beantwortet.",
  "support.error.notApproved": "Dieser Zugriff ist nicht offen.",
  "support.error.notActive": "Dieser Zugriff ist beendet.",

  "users.title": "Personen",
  "users.search": "Nach Name, Benutzername, E-Mail oder Kennung suchen",
  "users.searchHint":
    "Über alle Einrichtungen hinweg. Das ist die einzige Stelle der Konsole, die Mandanten übergreift — eine Supportfrage kommt meist als Name und sonst nichts.",
  "users.column.name": "Name",
  "users.column.role": "Rolle",
  "users.column.institution": "Einrichtung",
  "users.column.username": "Benutzername",
  "users.column.status": "Status",
  "users.empty": "Niemand passt dazu",
  "users.typeToSearch": "Mindestens zwei Zeichen eingeben",
  "users.noContent":
    "Konten und Zugänge. Nichts von dem, was jemand geschrieben hat, ist von hier erreichbar — mit oder ohne Supportzugriff.",
  "users.resetPassword": "Neues Passwort vergeben",
  "users.disable": "Deaktivieren",
  "users.enable": "Aktivieren",
  "users.disableTitle": "{name} deaktivieren",
  "users.disableBody":
    "Die Person kann sich nicht mehr anmelden. Gelöscht wird nichts, und ein erneutes Aktivieren stellt alles auf einmal wieder her.",
  "users.confirm": "Zur Bestätigung {name} eingeben",
  "users.mismatch": "Das ist nicht der Name.",
  "users.passwordIssued": "Neues Passwort für {name}: {password}",
  "users.passwordIssuedWarning": "Wird einmal angezeigt. Schreiben Sie es jetzt auf.",

  "retention.title": "Aufbewahrung",
  "retention.breach": "Daten, deren Löschung wir zugesagt haben, sind noch da",
  "retention.alarm.overdue":
    "{count} Sitzungen liegen jenseits des Zeitfensters ihrer Institution und wurden nicht entfernt.",
  "retention.alarm.missed":
    "Der nächtliche Lauf hat seit {hours} Stunden nichts gemeldet. Ein Lauf, der ausblieb, sieht genau aus wie einer, der nichts zu tun hatte.",
  "retention.alarm.failed": "Der letzte Lauf meldete {failures} Fehler, die er nicht überwinden konnte.",
  "retention.overdueLine": "{count} Sitzungen jenseits eines Fensters von {days} Tagen",
  "retention.clear": "Nichts liegt jenseits des Zeitfensters.",
  "retention.clearExplain":
    "Direkt in der Datenbank geprüft und nicht dem Bericht des letzten Laufs entnommen, der seine eigene Vollständigkeit nicht belegen kann.",
  "retention.lastRun": "Der letzte Lauf",
  "retention.ranAt": "Begonnen",
  "retention.duration": "Dauer",
  "retention.seconds": "{seconds} s",
  "retention.neverFinished": "Nie beendet",
  "retention.neverRun": "Es lief noch nie etwas. Das ist der schwerwiegendste Zustand auf dieser Seite.",
  "retention.sessionsDeleted": "Entfernte Sitzungen",
  "retention.eventsDeleted": "Entfernte Einträge",
  "retention.manual": "Jetzt ausführen, für eine Institution",
  "retention.manualExplain":
    "Für die Beantwortung eines Auskunftsersuchens: Die ehrliche Antwort fällt leichter, wenn das Zeitfenster bereits angewendet wurde, statt wenige Stunden bevor der nächtliche Lauf es angewendet hätte. Wird unter Ihrem Namen protokolliert.",
  "retention.institution": "Institution",
  "retention.run": "Zeitfenster jetzt anwenden",
  "retention.ran": "Erledigt. {count} Sitzungen entfernt.",

  "health.title": "Zustand",
  "health.jobsHealthy": "Alle {count} geplanten Aufgaben melden sich.",
  "health.jobsUnhealthy": "{count} geplante Aufgaben brauchen Aufmerksamkeit.",
  "health.jobsExplain":
    "Überwacht wird das Ausbleiben, nicht nur das Fehlschlagen: Eine Aufgabe, die nicht lief, ist von einer ohne Arbeit nicht zu unterscheiden. Diese Liste folgt deshalb den Aufgaben, die es geben soll, und nicht den vorhandenen Einträgen.",
  "health.jobs": "Geplante Aufgaben",
  "health.job.ok": "Meldet sich",
  "health.job.failing": "Schlägt fehl ({count} in Folge)",
  "health.job.missed": "Hat sich nicht gemeldet",
  "health.job.never": "Lief noch nie",
  "health.column.job": "Aufgabe",
  "health.column.state": "Zustand",
  "health.column.lastRun": "Zuletzt beendet",
  "health.column.duration": "Dauer",
  "health.column.expected": "Erwartet",
  "health.everyHours": "alle {hours} h",
  "health.sessions": "Laufende Stunden",
  "health.sessionsNow": "{active} laufen gerade — {online} online, {offline} offline.",
  "health.sessionsPrivacy":
    "Eine Zahl, nie eine Liste. Welche Klassen gerade spielen, ist Sache der Institution, und das zu lesen setzt eine Freigabe ihrer Inhaberin voraus.",
  "health.counts": "Heute",
  "health.countsExplain":
    "Nur aggregierte Zahlen — nie wer, nie was. Firestore-Regelablehnungen stehen hier nicht: Sie sind eine Cloud-Monitoring-Metrik, die keine Schnittstelle an eine Function übergibt. Abgelehnte Aufrufe messen etwas Verwandtes und heißen deshalb so.",
  "health.metric.callRefused": "Abgelehnte Aufrufe",
  "health.metric.emailSent": "Gesendete E-Mails",
  "health.metric.emailFailed": "Fehlgeschlagene E-Mails",
  "health.metric.webhookFailed": "Fehlgeschlagene Webhooks",
  "health.metric.webhookHandled": "Verarbeitete Webhooks",
  "health.spike": "Weit über den letzten vierzehn Tagen. Einen Blick wert.",
  "health.fortnight": "{total} in den letzten vierzehn Tagen",

  "migration.title": "Migrationen",
  "migration.intro":
    "Vor einem echten Lauf ist ein Probelauf erforderlich, und der Server ist es, der ablehnt — nicht diese Seite. Der Probelauf muss das Letzte sein, was mit einer Migration geschehen ist, denn einer von vor dem letzten echten Lauf beschreibt eine Datenbank, die es nicht mehr gibt.",
  "migration.idempotent":
    "Jede Migration ist idempotent geschrieben und wird in der Testsuite zweimal ausgeführt, um das zu belegen: Eine zweite Zone bedeutet irgendwann, alle noch einmal von vorn auszuführen.",
  "migration.empty": "Nichts zu migrieren",
  "migration.emptyBody": "Es wurde noch keine Migration geschrieben.",
  "migration.dryRun": "Probelauf",
  "migration.apply": "Anwenden",
  "migration.wouldChange": "Der Probelauf fand {count} zu ändernde Dokumente.",
  "migration.dryRunDone": "Probelauf beendet. {count} Dokumente würden sich ändern.",
  "migration.applied": "Angewendet. {count} Dokumente geändert.",
  "migration.blocked.noDryRun": "Bitte zuerst einen Probelauf durchführen.",
  "migration.blocked.dryRunStale":
    "Bitte erneut einen Probelauf durchführen — zuletzt geschah etwas anderes.",
  "migration.state.never": "Nie ausgeführt",
  "migration.state.dryRunOnly": "Nur Probelauf",
  "migration.state.applied": "Angewendet",
  "migration.state.failed": "Letzter Lauf fehlgeschlagen",
  "migration.mode.dry": "Probelauf",
  "migration.mode.live": "Angewendet",
  "migration.ok": "In Ordnung",
  "migration.failed": "Fehlgeschlagen",
  "migration.history": "Läufe von {id}",
  "migration.column.when": "Wann",
  "migration.column.mode": "Modus",
  "migration.column.affected": "Dokumente",
  "migration.column.outcome": "Ergebnis",
  "migration.confirmTitle": "Anwenden: {description}",
  "migration.confirmBody":
    "Das schreibt in jedes Dokument, das der Probelauf gezählt hat. Es ist so geschrieben, dass ein zweiter Lauf sicher ist, und bleibt eine Änderung an vielen Dokumenten auf einmal, die niemand einzeln ansieht.",
  "migration.confirmType": "Geben Sie {name} ein — die Zahl aus dem Probelauf",
  "migration.mismatch": "Das ist nicht die Zahl aus dem Probelauf.",
  "migration.error.unknown": "Es gibt keine Migration mit diesem Namen.",

  "ai.title": "Kosten der Assistenz",
  "ai.boundary.title": "Schülerdaten und das Modell",
  "ai.boundary.clean": "0 Grenzverletzungen, seit Beginn.",
  "ai.boundary.violated":
    "{count} Grenzverletzungen erfasst. Jede davon ist ein Fehler in unserem Code, kein Fehlalarm.",
  "ai.boundary.unaudited": "Es hat noch niemand geprüft.",
  "ai.boundary.explain":
    "Kein Modellaufruf darf etwas enthalten, das eine Schülerin oder einen Schüler identifiziert. Die Typunion macht es unausdrückbar, eine Laufzeitprüfung durchläuft jede Nutzlast, und eine tägliche Prüfung liest das gesamte Nutzungsprotokoll noch einmal.",
  "ai.boundary.lastAudit": "Zuletzt geprüft {when}: {checked} Aufrufe gelesen, keiner enthielt Daten von Teilnehmenden.",
  "ai.boundary.lastAuditEmpty":
    "Zuletzt gelaufen {when}, im Protokoll stand kein Aufruf. Alles seitdem ist ungeprüft.",
  "ai.boundary.stale":
    "Die tägliche Prüfung lief zuletzt vor {days} Tagen. Das ist ein anderes Problem als eine Verletzung, und es bedeutet, dass diese Zahl veraltet ist.",
  "ai.boundary.at": "{institution} · {feature} · {when}",
  "ai.boundary.reason.guardFired":
    "Ein Aufruf wurde abgelehnt, weil die Nutzlast nach personenbezogenen Daten aussah.",
  "ai.boundary.reason.unknownKind":
    "Ein Aufruf hat eine Form erfasst, die nicht zu den drei zulässigen gehört.",
  "ai.boundary.reason.oversizedField":
    "Ein Nutzungseintrag enthielt ein Feld, das lang genug für eine Nutzlast wäre.",

  "ai.month": "Monat",
  "ai.nothing": "Nichts in diesem Monat",
  "ai.ms": "{ms} ms",
  "ai.callsWithFailures": "{calls} ({failures} fehlgeschlagen)",
  "ai.spend.title": "Ausgaben",
  "ai.spend.total": "Dieser Monat",
  "ai.spend.calls": "Aufrufe",
  "ai.spend.failureRate": "Fehlgeschlagen",
  "ai.spend.p90": "Langsamstes Zehntel",
  "ai.feature.authoring": "Eine Szene schreiben",
  "ai.feature.importMap": "Eine Klassenliste lesen",
  "ai.feature.statsSummary": "Statistik zusammenfassen",
  "ai.byFeature": "Nach Zweck",
  "ai.byModel": "Nach Modell",
  "ai.byInstitution": "Nach Institution",
  "ai.column.name": "Name",
  "ai.column.calls": "Aufrufe",
  "ai.column.cost": "Kosten",
  "ai.column.share": "Anteil",
  "ai.column.model": "Modell",
  "ai.column.errorRate": "Fehler",
  "ai.column.p50": "Median",
  "ai.column.p90": "Langsamstes Zehntel",
  "ai.alert.title": "Nahe am Limit",
  "ai.alert.approaching": "{institution} hat {spent} von {budget} ausgegeben.",
  "ai.alert.exhausted":
    "{institution} hat {spent} von {budget} ausgegeben. Die Assistenz antwortet dort nicht mehr.",
  "ai.outlier.title": "Weit über den anderen",
  "ai.outlier.explain":
    "Meist eine hängende Wiederholungsschleife und nichts Absichtliches — ansehen lohnt sich trotzdem.",
  "ai.outlier.line": "{factor}× der Median — {spent} gegenüber {median}.",
  "ai.health.title": "Der Anbieter",
  "ai.health.fallbackNormal":
    "{rate} der Klassenlisten-Importe liefen über den eingebauten Leser. Das ist der Rückfall, der seine Arbeit tut.",
  "ai.health.fallbackHigh":
    "{rate} von {calls} Klassenlisten-Importen liefen über den eingebauten Leser. Die Importe funktionieren — das Modell nicht.",

  "legal.title": "Rechtsdokumente",
  "legal.intro":
    "Alle Dokumente aus dem Register, ob veröffentlicht oder nicht. Ein Dokument, das nur im Repository existiert, gilt trotzdem für jemanden — und diese Institution merkt es erst, wenn sie eine Stunde starten will.",
  "legal.tabs": "Bereiche",
  "legal.tab.documents": "Dokumente",
  "legal.tab.matrix": "Wer bekommt was",
  "legal.tab.acceptances": "Zustimmungen",
  "legal.tab.coverage": "Blockiert",
  "legal.empty": "Keine Treffer für diese Filter",
  "legal.emptyBody": "Filter erweitern oder das Register im Repository prüfen.",
  "legal.filter.type": "Art",
  "legal.filter.jurisdiction": "Land",
  "legal.filter.language": "Sprache",
  "legal.filter.all": "Alle",
  "legal.column.document": "Dokument",
  "legal.column.type": "Art",
  "legal.column.jurisdictions": "Länder",
  "legal.column.segments": "Gilt für",
  "legal.column.version": "Fassung",
  "legal.column.state": "Stand",
  "legal.column.published": "Veröffentlicht",
  "legal.column.action": "Aktion",
  "legal.column.required": "Blockiert Stunden",
  "legal.versionOf": "{published} veröffentlicht, {registry} im Register",
  "legal.versionNone": "{registry} im Register",
  "legal.publishAction": "Veröffentlichen",
  "legal.published":
    "Veröffentlicht. {affected} Institutionen betroffen, {notified} Inhaberinnen und Inhaber benachrichtigt.",

  "legal.type.tos": "Nutzungsbedingungen",
  "legal.type.dpa": "Data Processing Agreement",
  "legal.type.avv": "Auftragsverarbeitungsvertrag",
  "legal.type.ferpa_addendum": "FERPA-Zusatz",
  "legal.type.state_addendum": "Bundesstaat-Zusatz",
  "legal.type.privacy": "Datenschutzerklärung",
  "legal.type.privacy_child": "Datenschutz in einfacher Sprache",
  "legal.type.impressum": "Impressum",
  "legal.type.parental_consent_template": "Einwilligungsvorlage für Eltern",
  "legal.type.law25_addendum": "Law-25-Zusatz",

  "legal.state.inReview": "In der rechtlichen Prüfung",
  "legal.state.unpublished": "Nie veröffentlicht",
  "legal.state.current": "Veröffentlicht und aktuell",
  "legal.state.behind": "Eine Fassung hinter dem Register",
  "legal.state.diverged": "Vor dem Register",

  "legal.blocked.inReview":
    "Noch nicht freigegeben. Eine Veröffentlichung würde einen Text hinter ein „Ich stimme zu“ stellen, den niemand geprüft hat.",
  "legal.blocked.versionMismatch":
    "Die Veröffentlichung ergäbe Fassung {version}, die Website zeigt {registry}. Bitte zuerst legal/documents.json im Repository ändern und die Website ausrollen — eine Institution, die eine Fassung liest und für eine andere als zustimmend erfasst wird, ist genau der Fehler, den diese Prüfung verhindert.",
  "legal.blocked.noDate": "Das Register enthält kein Gültigkeitsdatum für dieses Dokument.",

  "legal.publish.title": "{title} veröffentlichen",
  "legal.publish.irreversible":
    "Eine veröffentlichte Fassung lässt sich weder ändern noch zurückziehen. Ein Fehler wird korrigiert, indem eine weitere Fassung veröffentlicht wird.",
  "legal.publish.consequence":
    "Jede betroffene Institution, die eine frühere Fassung angenommen hat, muss erneut lesen und zustimmen, bevor die nächste Stunde starten kann. Die Inhaberinnen und Inhaber werden per E-Mail informiert.",
  "legal.publish.fromRegistry":
    "Diese Werte stammen aus legal/documents.json. Nichts davon wird hier von Hand eingetippt, denn eine vertippte Länderliste würde veröffentlicht.",
  "legal.publish.preview": "Was gelesen wird",
  "legal.publish.previewUnavailable":
    "Die Seite konnte nicht geladen werden. Veröffentlichen ist trotzdem möglich; empfehlen würde es niemand.",
  "legal.publish.typeVersion": "Zum Veröffentlichen {version} eingeben",
  "legal.publish.mismatch": "Das ist nicht die Fassungsnummer.",
  "legal.publish.confirm": "Fassung {version} veröffentlichen",

  "legal.matrix.title": "Wer bekommt was",
  "legal.matrix.intro":
    "Welche Dokumente für eine Institution gelten, ergibt sich aus Land und Art — niemand wählt das aus. Das ist die riskanteste Konfiguration im Produkt: eine falsche Zelle bedeutet eine Institution ohne Auftragsverarbeitungsvertrag.",
  "legal.matrix.readOnly":
    "Nur lesbar, und vom Backend aufgelöst statt von dieser Seite berechnet. Die Tabelle steht in geprüftem Code mit einem Test über jede Zeile; eine Konsole, die sie ändern könnte, wäre eine zweite Instanz, die niemand im Diff sieht.",
  "legal.matrix.preview": "Ein Beispiel zeigen",
  "legal.matrix.previewIntro":
    "Wählen Sie ein Profil, dessen Antwort Sie kennen, und lesen Sie, was zurückkommt. So wird diese Tabelle geprüft.",
  "legal.matrix.country": "Land",
  "legal.matrix.segment": "Art",
  "legal.matrix.resolvedFor": "Für {country}, {segment}: {count} Dokumente.",
  "legal.matrix.required": "Blockiert Stunden",
  "legal.matrix.reference": "Zur Information",
  "legal.matrix.none": "Gilt nicht",
  "legal.matrix.publishedAs": "{docId}, Fassung {version}",
  "legal.matrix.notPublished": "Nichts veröffentlicht",
  "legal.matrix.unexpected":
    "Für dieses Profil veröffentlicht, aber nicht gefordert: {documents}. Jemand soll etwas unterschreiben, das für ihn nicht gilt.",
  "legal.matrix.sound": "Alles, was dieses Profil braucht, ist veröffentlicht.",
  "legal.matrix.unsound":
    "Für {count} erforderliche Dokumente ist nichts veröffentlicht. Institutionen mit diesem Profil können nicht aufgenommen werden.",
  "legal.matrix.wholeTable": "Alle Länder",
  "legal.matrix.legend":
    "{required} blockiert Stunden · {reference} zur Information · {none} gilt nicht",

  "legal.acceptances.title": "Zustimmungen",
  "legal.acceptances.intro":
    "Wer wann und von welcher Adresse zugestimmt hat. Das ist die Antwort, wenn eine Datenschutzbeauftragte fragt, was ihre Institution unterschrieben hat.",
  "legal.acceptances.immutable":
    "Nur lesbar, und nicht, weil die Schaltflächen fehlen: Diese Einträge dürfen weder angelegt noch geändert noch gelöscht werden. Ein Nachweis, den jeder ändern kann, ist kein Nachweis.",
  "legal.acceptances.search": "Suche",
  "legal.acceptances.searchHint": "Institution, Dokument oder Kennung der zustimmenden Person.",
  "legal.acceptances.export": "{count} Zeilen exportieren",
  "legal.acceptances.empty": "Keine Treffer",
  "legal.acceptances.emptyBody": "Dazu hat niemand zugestimmt.",
  "legal.acceptances.column.institution": "Institution",
  "legal.acceptances.column.institutionId": "Institutions-ID",
  "legal.acceptances.column.uid": "Zugestimmt von",
  "legal.acceptances.column.docId": "Dokument",
  "legal.acceptances.column.version": "Fassung",
  "legal.acceptances.column.acceptedAt": "Wann",
  "legal.acceptances.column.ip": "Von",

  "legal.coverage.title": "Vom Unterricht ausgeschlossen",
  "legal.coverage.intro":
    "Institutionen, die wegen eines offenen Dokuments keine Stunde starten können. Dort steht jemand vor einer Klasse — das hier ist eine Arbeitsliste, kein Bericht.",
  "legal.coverage.institutions": "Institutionen",
  "legal.coverage.blockedInstitutions": "Blockiert",
  "legal.coverage.blockedGroups": "Gruppen ohne Start",
  "legal.coverage.oursCount": "Wartet auf uns",
  "legal.coverage.gapsTitle": "Dokumente, die niemand veröffentlicht hat",
  "legal.coverage.gapsBody":
    "Das liegt an uns, nicht an den Institutionen. Sie können nichts tun, und keine E-Mail hilft.",
  "legal.coverage.search": "Suche",
  "legal.coverage.empty": "Niemand ist blockiert",
  "legal.coverage.emptyBody": "Jede Institution hat alles Geltende angenommen.",
  "legal.coverage.column.institution": "Institution",
  "legal.coverage.column.profile": "Profil",
  "legal.coverage.column.fault": "Wartet auf",
  "legal.coverage.column.outstanding": "Was fehlt",
  "legal.coverage.column.blocked": "Gruppen blockiert",
  "legal.coverage.profile": "{country}, {segment}",
  "legal.coverage.ours": "Uns",
  "legal.coverage.theirs": "Die Institution",
  "legal.coverage.clear": "Nichts",
  "legal.coverage.blockedOf": "{blocked} von {active}",

  "common.language.en": "Englisch",
  "common.language.de": "Deutsch",
};
